// @ts-check

import { Natures, QuestionIds } from "consts";
import { isArray } from "utils";

/**
 * Shared helpers for the age-question variants (slider, picker, numpad). These
 * all map a numeric age onto the same radio options, so the range bounds and the
 * option-matching/restore logic live here rather than being duplicated per variant.
 *
 * @typedef {import('types/api-responses').QuestionOption} QuestionOption
 */

/**
 * Discriminant values for the age-question variant. Exactly one of these is
 * returned by getAgeVariant when the question is the age question; null means
 * the question is not the age question at all.
 *
 * @type {{ Slider: 'slider', Picker: 'picker', Numpad: 'numpad' }}
 */
export const AgeVariant = {
  Slider: "slider",
  Picker: "picker",
  Numpad: "numpad",
};

/**
 * Returns which age-question variant to render, or null if this is not the age
 * question. AgeVariant.Slider is the base case — it wins when the question ID
 * matches but no specific test nature is present.
 *
 * @param {string|number} questionId
 * @param {string[]} natures
 * @returns {'slider'|'picker'|'numpad'|null}
 */
export const getAgeVariant = (questionId, natures) => {
  if (questionId !== QuestionIds.Age) return null;
  const naturesSet = new Set(isArray(natures) ? natures : []);
  if (naturesSet.has(Natures.TestAgePicker)) return AgeVariant.Picker;
  if (naturesSet.has(Natures.TestAgeNumpad)) return AgeVariant.Numpad;
  return AgeVariant.Slider;
};

/** Youngest selectable age across the age-question variants. */
export const MIN_AGE = 13;

/** Oldest selectable age; the top bucket ("90 or above") maps here. */
export const MAX_AGE = 90;

/**
 * Clamps an age into the selectable [MIN_AGE, MAX_AGE] range.
 *
 * @param {number} val
 * @returns {number}
 */
export const clamp = (val) => Math.min(MAX_AGE, Math.max(MIN_AGE, val));

/**
 * Maps a selected age to the question option whose text begins with that age.
 * Matching uses parseInt so bucket labels like "90 or above", "90+", or "90 and older"
 * all resolve to age 90 without special-casing.
 *
 * @param {number} age
 * @param {QuestionOption[]} options
 * @returns {QuestionOption | null}
 */
export const findOptionForAge = (age, options) => {
  if (!options || !options.length) {
    return null;
  }
  return options.find((o) => parseInt(o.text, 10) === age) ?? null;
};

/**
 * Derives the initial age to display from a previously-selected option (if any).
 * Used to restore variant state when the user navigates back to resubmit an age question.
 * Clamps to MIN_AGE/MAX_AGE so an out-of-range prior answer still maps to a valid value.
 *
 * @param {QuestionOption[]} options
 * @returns {number | null}
 */
export const getInitialAgeFromOptions = (options) => {
  const selected = options && options.find((o) => o.selected);
  if (!selected) {
    return null;
  }
  const parsedAge = parseInt(selected.text, 10);
  if (isNaN(parsedAge)) {
    return null;
  }
  return clamp(parsedAge);
};
